import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { dayjs } from "@thalamos/common";
import type {
  IncidentWorkItemId,
  IncidentWorkItemWithContext,
} from "@thalamos/vision-common";
import {
  calculateEndIncidentActions,
  endIncidentDestination,
} from "@thalamos/vision-common";

import api from "../api/index.js";
import { useUser } from "../Context/UserContext.js";
import { endIncidentRoute } from "../routing/endIncidentRoute.js";
import { useToastNotifications } from "./useToastNotifications.js";

/**
 * Sends the officer on from an ending page, replacing it in history, once
 * ending their incident no longer goes there: the end type can move on, or the
 * incident end, after they were sent to it.
 */
export function useEndIncidentGuard(
  page: "finishForm" | "preEndIncident",
  incidentWorkItemId: IncidentWorkItemId,
  loadedIncident: IncidentWorkItemWithContext | undefined,
) {
  const { user } = useUser();
  const navigate = useNavigate();
  const notifications = useToastNotifications();
  const { t } = useTranslation();
  const [rereadIncident, setRereadIncident] =
    useState<IncidentWorkItemWithContext>();
  // Once this page's own submit has ended the incident, it can no longer be
  // ended, which must not read as a reason to leave.
  const [endedIncidentWorkItemId, setEndedIncidentWorkItemId] =
    useState<IncidentWorkItemId>();

  const destinationOf = (incidentWorkItem: IncidentWorkItemWithContext) =>
    user === undefined
      ? page
      : endIncidentDestination(
          calculateEndIncidentActions({
            incidentWorkItem,
            user,
            asOfDateTime: dayjs(),
          }),
        );

  // The page stays mounted when only its route's incident id changes, and the
  // request hook serves the previous incident until the next one loads.
  const incident = [rereadIncident, loadedIncident].find(
    (candidate) => candidate?.incidentWorkItem.id === incidentWorkItemId,
  );
  const destination =
    endedIncidentWorkItemId === incidentWorkItemId || incident === undefined
      ? page
      : destinationOf(incident);

  useEffect(() => {
    if (destination === page) return;
    if (destination === "cannotEnd") {
      notifications.show(t("incidentWorkItem.noLongerEndable"), {
        severity: "info",
      });
    }
    navigate(endIncidentRoute(incidentWorkItemId, destination), {
      replace: true,
    });
  }, [destination, page, incidentWorkItemId, navigate, notifications, t]);

  return {
    isLeaving: destination !== page,
    /** After a refused submit: leaves, and resolves true, if the incident has moved on. */
    leaveIfMovedOn: async () => {
      const latest = await api.incidentWorkItem.get(incidentWorkItemId);
      if (
        latest.status !== 200 ||
        latest.data === null ||
        destinationOf(latest.data) === page
      ) {
        return false;
      }
      setRereadIncident(latest.data);
      return true;
    },
    markEnded: () => {
      setEndedIncidentWorkItemId(incidentWorkItemId);
    },
  };
}
